import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  CheckCheck,
  ChevronLeft,
  Download,
  FileText,
  Grid2X2,
  LoaderCircle,
  LockKeyhole,
  Printer,
  RotateCcw,
  ScanLine,
  UploadCloud,
  X,
} from 'lucide-react';
import type { GridSettings, LabelPreview, LabelSegment } from './types';
import { pointsToMillimeters } from './lib/geometry';

type Toast = { type: 'success' | 'error'; message: string } | null;
type PdfAction = 'download' | 'print';

function toPdfBlob(bytes: Uint8Array) {
  const safeBytes = Uint8Array.from(bytes);
  return new Blob([safeBytes.buffer], { type: 'application/pdf' });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function printBlob(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.title = 'چاپ لیبل‌ها';
  iframe.style.position = 'fixed';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.src = url;
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    window.setTimeout(() => {
      iframe.remove();
      URL.revokeObjectURL(url);
    }, 60_000);
  };
  document.body.appendChild(iframe);
}

function baseName(filename: string) {
  return filename.replace(/\.pdf$/i, '').replace(/[^\p{L}\p{N}_-]+/gu, '-');
}

export default function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const generationRef = useRef(0);
  const [fileName, setFileName] = useState('');
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [grid, setGrid] = useState<GridSettings>({ rows: 1, columns: 2 });
  const [previews, setPreviews] = useState<LabelPreview[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [renderProgress, setRenderProgress] = useState(0);
  const [isRendering, setIsRendering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [busyAction, setBusyAction] = useState<PdfAction | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  const showToast = useCallback((nextToast: Toast) => {
    setToast(nextToast);
    window.setTimeout(() => setToast(null), 3400);
  }, []);

  const loadFile = useCallback(async (file: File) => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      showToast({ type: 'error', message: 'فقط فایل PDF قابل پردازش است.' });
      return;
    }
    if (file.size > 80 * 1024 * 1024) {
      showToast({ type: 'error', message: 'حجم فایل باید کمتر از ۸۰ مگابایت باشد.' });
      return;
    }

    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const { inspectPdf } = await import('./lib/pdf');
      const info = await inspectPdf(bytes);
      setFileName(file.name);
      setPageCount(info.pageCount);
      setPdfBytes(bytes);
    } catch {
      showToast({ type: 'error', message: 'این PDF قابل خواندن نیست یا رمزگذاری شده است.' });
    }
  }, [showToast]);

  useEffect(() => {
    if (!pdfBytes) return;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setIsRendering(true);
    setRenderProgress(0);
    setPreviews([]);

    import('./lib/previews')
      .then(({ renderLabelPreviews }) => renderLabelPreviews(pdfBytes, grid, setRenderProgress))
      .then((items) => {
        if (generationRef.current !== generation) return;
        setPreviews(items);
        setSelected(new Set(items.map((item) => item.id)));
      })
      .catch(() => {
        if (generationRef.current === generation) {
          showToast({ type: 'error', message: 'ساخت پیش‌نمایش انجام نشد.' });
        }
      })
      .finally(() => {
        if (generationRef.current === generation) setIsRendering(false);
      });
  }, [pdfBytes, grid, showToast]);

  const chosenSegments = useMemo<LabelSegment[]>(
    () => previews
      .filter((preview) => selected.has(preview.id))
      .map(({ id, pageIndex, row, column }) => ({ id, pageIndex, row, column })),
    [previews, selected],
  );

  const labelDimensions = previews[0]
    ? `${pointsToMillimeters(previews[0].width).toFixed(1)} × ${pointsToMillimeters(previews[0].height).toFixed(1)} میلی‌متر`
    : '—';

  const toggleSelection = (id: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runPdfAction = async (action: PdfAction) => {
    if (!pdfBytes || chosenSegments.length === 0) return;
    setBusyAction(action);
    try {
      const { createIndependentLabelsPdf } = await import('./lib/pdf');
      const output = await createIndependentLabelsPdf(pdfBytes, chosenSegments, grid);
      const blob = toPdfBlob(output);

      if (action === 'download') {
        downloadBlob(blob, `${baseName(fileName)}-labels.pdf`);
        showToast({ type: 'success', message: 'PDF لیبل‌ها آماده شد.' });
      } else {
        printBlob(blob);
        showToast({ type: 'success', message: 'پنجره چاپ لیبل‌ها باز شد.' });
      }
    } catch {
      showToast({ type: 'error', message: 'ساخت خروجی لیبل‌ها انجام نشد.' });
    } finally {
      setBusyAction(null);
    }
  };

  const reset = () => {
    generationRef.current += 1;
    setPdfBytes(null);
    setFileName('');
    setPageCount(0);
    setPreviews([]);
    setSelected(new Set());
    setRenderProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark"><ScanLine size={20} /></span>
          <div><strong>کافینا لیبل</strong><small>LABEL PRINTER</small></div>
        </div>
        <span className="privacy"><LockKeyhole size={14} /> پردازش امن داخل مرورگر</span>
      </header>

      <main>
        <section className="intro">
          <span className="intro-kicker">چاپ مستقیم لیبل دیجی‌کالا</span>
          <h1>لیبل‌ها را جدا کن.<br />همان اندازه چاپ بگیر.</h1>
          <p>هر لیبل در یک صفحه مستقل و با ابعاد واقعی برای لیبل‌پرینتر آماده می‌شود.</p>
        </section>

        <section className={`workspace ${pdfBytes ? 'has-file' : ''}`}>
          {!pdfBytes ? (
            <div
              className={`dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                const file = event.dataTransfer.files[0];
                if (file) void loadFile(file);
              }}
            >
              <span className="upload-icon"><UploadCloud size={30} /></span>
              <h2>فایل PDF را اینجا رها کن</h2>
              <p>فایل فقط روی همین دستگاه پردازش می‌شود.</p>
              <button className="primary-button" onClick={() => inputRef.current?.click()}>
                انتخاب فایل <ChevronLeft size={18} />
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void loadFile(file);
                }}
              />
            </div>
          ) : (
            <>
              <div className="file-toolbar">
                <div className="file-identity">
                  <span className="file-icon"><FileText size={20} /></span>
                  <div><strong>{fileName}</strong><small>{pageCount.toLocaleString('fa-IR')} صفحه</small></div>
                </div>

                <div className="split-controls">
                  <span><Grid2X2 size={15} /> برش هر صفحه</span>
                  <label>ستون
                    <select value={grid.columns} onChange={(event) => setGrid((current) => ({ ...current, columns: Number(event.target.value) }))}>
                      {[1, 2, 3, 4].map((value) => <option key={value} value={value}>{value.toLocaleString('fa-IR')}</option>)}
                    </select>
                  </label>
                  <label>سطر
                    <select value={grid.rows} onChange={(event) => setGrid((current) => ({ ...current, rows: Number(event.target.value) }))}>
                      {[1, 2, 3, 4].map((value) => <option key={value} value={value}>{value.toLocaleString('fa-IR')}</option>)}
                    </select>
                  </label>
                </div>

                <button className="icon-button" onClick={reset} title="انتخاب فایل جدید"><RotateCcw size={17} /></button>
              </div>

              <div className="selection-toolbar">
                <div className="selection-summary">
                  <strong>{selected.size.toLocaleString('fa-IR')}</strong>
                  <span>از {previews.length.toLocaleString('fa-IR')} لیبل انتخاب شده</span>
                  <i />
                  <span>{labelDimensions}</span>
                </div>
                <div className="selection-actions">
                  <button onClick={() => setSelected(new Set(previews.map((item) => item.id)))}><CheckCheck size={15} /> همه</button>
                  <button onClick={() => setSelected(new Set())}><X size={15} /> هیچ‌کدام</button>
                </div>
              </div>

              {isRendering ? (
                <div className="render-state">
                  <LoaderCircle className="spin" size={24} />
                  <span>در حال آماده‌سازی لیبل‌ها</span>
                  <div className="progress-track"><span style={{ width: `${renderProgress}%` }} /></div>
                </div>
              ) : (
                <div className="label-grid">
                  {previews.map((preview, index) => {
                    const isSelected = selected.has(preview.id);
                    return (
                      <button
                        key={preview.id}
                        type="button"
                        className={`label-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleSelection(preview.id)}
                        aria-pressed={isSelected}
                      >
                        <span className="label-index">{(index + 1).toLocaleString('fa-IR')}</span>
                        <span className="check-mark">{isSelected && <Check size={14} strokeWidth={3} />}</span>
                        <img src={preview.dataUrl} alt={`پیش‌نمایش لیبل ${index + 1}`} />
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="action-bar">
                <div className="print-note"><Printer size={17} /><span><strong>هر لیبل یک صفحه مستقل</strong><small>برای چاپ، Scale را روی ۱۰۰٪ قرار بده.</small></span></div>
                <div className="main-actions">
                  <button disabled={!selected.size || !!busyAction} onClick={() => void runPdfAction('download')}>
                    {busyAction === 'download' ? <LoaderCircle className="spin" /> : <Download />}
                    دانلود PDF
                  </button>
                  <button className="print-button" disabled={!selected.size || !!busyAction} onClick={() => void runPdfAction('print')}>
                    {busyAction === 'print' ? <LoaderCircle className="spin" /> : <Printer />}
                    چاپ لیبل‌ها
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === 'success' ? <Check size={17} /> : <X size={17} />}
          {toast.message}
        </div>
      )}
    </div>
  );
}
